import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";
import { Globe2 } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("auth.welcome_back"));
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.invalid_credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center p-6 bg-slate-50/50" data-testid="login-page">
      <div className="w-full max-w-md mec-card p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-md bg-slate-900 flex items-center justify-center">
            <Globe2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900">{t("auth.login_title")}</div>
            <div className="text-xs text-slate-500">{t("auth.login_sub")}</div>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-sm">{t("auth.email")}</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="login-email" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-sm">{t("auth.password")}</Label>
              <Link
                to="/forgot-password"
                className="text-xs text-[#004EEB] hover:underline font-medium"
                data-testid="link-forgot-password"
              >
                {t("auth.forgot_password")}
              </Link>
            </div>
            <PasswordInput
              id="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="login-password"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#004EEB] hover:bg-[#003BCC] text-white" data-testid="login-submit">
            {loading ? t("auth.logging_in") : t("auth.login_btn")}
          </Button>
        </form>
        <div className="mt-6 text-center text-sm text-slate-600">
          {t("auth.no_account")}{" "}
          <Link to="/register" className="text-[#004EEB] hover:underline font-medium" data-testid="link-register">
            {t("auth.create_account")}
          </Link>
        </div>
      </div>
    </div>
  );
}
